import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { organization as orgApi } from 'utilities/api';
import { useHistory } from 'react-router-dom';

import { useAuth } from 'utilities/base-auth';

import { Formik } from 'formik';

import PortalModal from 'components/Modals/PortalModal';
import { Context } from 'components/Store';
import ErrorMessageConsole from 'components/ErrorHandler/ErrorMessageConsole';
import FormikInput from 'screens/Organizations/components/FormikInput';
import catchCancel from 'helpers/catchCancel';
import { parseServerError } from 'helpers/errorHelpers';
import { selectOrganization } from 'utilities/updated-permissions';
import { paths } from 'routes/paths';

const CreateOrganizationModal = ({
  open = false,
  handleCancel,
  onSelectConfirm = /** @type {((...args: any[]) => any) | undefined} */ (undefined)
}) => {
  const [apiError] = useState();
  const [isSaving, setIsSaving] = useState(false);

  const [, dispatch] = useContext(Context);

  const { fetchUserProfile } = useAuth();
  const history = useHistory();
  const { user } = useAuth();

  const handleCreateOrganization = async formData => {
    setIsSaving(true);
    orgApi.updateRequestInterceptor({}, () => ``);
    const { promise } = orgApi.create(formData);
    promise
      .then(({ data }) => {
        // This will reinitialize the application because the user profile has to be reset in order to get permissions
        fetchUserProfile();
        return data;
      })
      .then(data => {
        // Switching straight to the org just created otherwise bypasses an
        // unsaved-changes guard entirely, the same way selecting an existing
        // org used to — deferring behind onSelectConfirm when the caller
        // supplies one lets it block the switch on a dirty form.
        const selectOrgWrapper = () => {
          localStorage.setItem('selectedCropSeasons', JSON.stringify([]));
          selectOrganization(dispatch, { name: data.name, id: data.id }, user);
          history.go(`${paths.organizations}/${data.id}`);
        };
        if (onSelectConfirm) {
          onSelectConfirm(data, selectOrgWrapper);
          setIsSaving(false);
          // Deferring the switch behind onSelectConfirm means history.go no
          // longer necessarily unmounts this modal the way it used to, so it
          // has to close itself explicitly instead of relying on that
          // navigation as a side effect.
          handleCancel();
        } else {
          selectOrgWrapper();
        }
      })
      .catch(catchCancel)
      .catch(err => {
        setIsSaving(false);
        parseServerError(dispatch)(err);
      });
  };

  return (
    <div data-testid="create-organiaztion-modal">
      <Formik
        initialValues={{
          name: '',
          languageCountry: 'en-US',
          timeZone: 'GMT',
          areaUnit: 'acres'
        }}
        validate={values => {
          const errors = {};
          const regex = /^\s+$/;
          if (!values.name.length > 0 || regex.test(values.name)) {
            errors.name = 'Required';
          }
          return errors;
        }}
        onSubmit={handleCreateOrganization}
      >
        {({ errors, handleSubmit }) => {
          return (
            <>
              <PortalModal
                cancelLabel="Cancel"
                onConfirm={handleSubmit}
                confirmLabel="Save"
                open={open}
                close={handleCancel}
                title="Create new organization"
                isSaving={isSaving}
              >
                <>
                  <div className="pr-6 mt-12">
                    <FormikInput autoFocus label="Name" name="name" />
                  </div>
                  <div className="flex justify-end pb-4 mr-5">
                    <ErrorMessageConsole
                      error={apiError}
                      consoleMessage={apiError}
                    />
                  </div>
                </>
              </PortalModal>
            </>
          );
        }}
      </Formik>
    </div>
  );
};

CreateOrganizationModal.propTypes = {
  handleCancel: PropTypes.func.isRequired,
  open: PropTypes.bool,
  onSelectConfirm: PropTypes.func
};

export default CreateOrganizationModal;
